import {
  isPeriodPickerValue,
  type MyuiPeriodPickerOption,
} from '@myre/ui-primitive-forms';

import { isObject } from 'lodash-es';

import {
  type Filter,
  type FilterOption,
  type FilterValue,
  type PeriodPickerFilter,
  type SelectFilter,
} from '../interfaces/filters/base.filter';
import { type DateFilter } from '../interfaces/filters/date.filter';
import { type SearchFilter } from '../interfaces/filters/search.filter';
import { type SwitchFilter } from '../interfaces/filters/switch.filter';

export function isSearchFilter(filter: Filter<string>): filter is SearchFilter {
  return (filter as SearchFilter).type === 'search';
}

export function isSelectedOption(option: unknown): option is FilterValue {
  return (
    ['string', 'number', 'boolean'].includes(typeof option) ||
    isPeriodPickerValue(option) ||
    (Array.isArray(option) && option.every(opt => isSelectedOption(opt)))
  );
}

export function isFilter<Key extends string = string>(
  value: unknown,
): value is SelectFilter<Key> {
  return (
    isObject(value) &&
    'type' in value &&
    ['select', 'multiselect'].includes(String(value.type))
  );
}

export function isSelectFilter(value: unknown): value is SelectFilter {
  const types = ['select', 'multiselect'];

  return (
    isObject(value) && 'type' in value && types.includes(String(value.type))
  );
}

export function isPeriodPickerFilter(
  value: unknown,
): value is PeriodPickerFilter {
  return isObject(value) && 'type' in value && value.type === 'period-picker';
}

export function isDateFilter(value: unknown): value is DateFilter {
  return isObject(value) && 'type' in value && value.type === 'date';
}

export function isSwitchFilter(value: unknown): value is SwitchFilter {
  return isObject(value) && 'type' in value && value.type === 'switch';
}

/** Filter kinds the configuration section (`configFilter: true`) knows how to render. */
export function isConfigurableFilter(
  value: unknown,
): value is SelectFilter | SwitchFilter {
  return isSelectFilter(value) || isSwitchFilter(value);
}

export function isFilterOption(option: unknown): option is FilterOption {
  const keys = ['label', 'value'];

  return (
    !isPeriodPickerOption(option) &&
    isObject(option) &&
    keys.every(key => key in option)
  );
}

function isPeriodPickerOption(
  option: unknown,
): option is MyuiPeriodPickerOption {
  const keys = ['key', 'label', 'compute'];

  return isObject(option) && keys.every(key => key in option);
}
